import { useNavigate } from 'react-router-dom';
import { VehiclesStatisticsByDriver } from '../../../API/types/Vehicles';
import useVehicleThumbnail from '../../../theme/useVehicleThumbnail';
import Tags from '../Tags';
import { ListItemWrapper } from '../../ui/ListItemWrapper/ListItemWrapper';
import { IconKeys } from '../Icon/IconKeys';
import { generateDistance } from '../../../utils/unitsUtils';
import { generateScoreColor, generateVehicleStatusColor, getScoreLabel } from '../../../utils/commonUtils';
import { convertMsToTime } from '../../../utils/dateUtils';
import { useEntityScores } from '../../../API/hooks/useEntityScores';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import useGetVehicleById from '../../../API/hooks/vehicles/useGetVehicleById';
import { VehicleStatus } from '../../../constants/VehicleStatus';

const PERIOD_TO_SCORE_FIELD: Record<string, 'score' | 'scoreDaily' | 'scoreWeekly' | 'scoreMonthly'> = {
  daily: 'scoreDaily',
  weekly: 'scoreWeekly',
  monthly: 'scoreMonthly',
};

type Props = {
  item?: VehiclesStatisticsByDriver;
  period?: string;
};

function VehicleStatsItem({ item, period }: Props): JSX.Element {
  const navigate = useNavigate();
  const vehicleThumbnail = useVehicleThumbnail();
  const fleetId = useGetFleetId();
  const {
    vehicleId,
    vehicle,
    distanceInKm,
    alertCount,
    durationInMs,
  } = item!;

  const scoreField = PERIOD_TO_SCORE_FIELD[period ?? ''] ?? 'score';
  const { scores } = useEntityScores(fleetId, vehicleId ? [vehicleId] : [], 'vehicle');
  const computedScores = vehicleId ? scores.get(vehicleId) ?? null : null;
  const computedScore = computedScores ? (computedScores[scoreField] ?? null) : null;
  const displayScore = computedScore !== null ? computedScore : (vehicle?.score ?? null);
  const periodLabel = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' }[period ?? ''] ?? 'All time';
  const { data: liveVehicle } = useGetVehicleById(vehicleId, fleetId, !!vehicleId);
  const vehicleStatus = liveVehicle?.status ?? vehicle?.status;

  return vehicle ? (
    <ListItemWrapper
      onClick={() => navigate(`/vehicles/${vehicleId}`)}
    >
      {displayScore !== null && displayScore > 0 && (
      <Tags.Status label={`${getScoreLabel(displayScore)} · ${periodLabel}`} backgroundColor={generateScoreColor(displayScore)} />
      )}
      <Tags.ItemOverview
        avatar={vehicleThumbnail}
        avatarAlt="vehicle"
        title={vehicle.licensePlateNumber}
        subtitle={`${vehicle.manufacturer ? (`${vehicle.manufacturer} `) : ''}${vehicle.model ? (`${vehicle.model} `) : ''}`}
      />
      <div className="tags-container" />
      <Tags.Status
        label={vehicleStatus ? VehicleStatus[vehicleStatus as keyof typeof VehicleStatus] : 'No driving status'}
        backgroundColor={generateVehicleStatusColor(vehicleStatus)}
        title="Vehicle status"
      />
      <Tags.LabeledIcon
        label={convertMsToTime(durationInMs)}
        icon={IconKeys.TIME}
      />
      <Tags.LabeledIcon
        label={generateDistance(distanceInKm, false)}
        icon={IconKeys.DISTANCE}
      />
      <Tags.LabeledIcon
        flexible
        label={String(alertCount)}
        icon={IconKeys.ALERT_CIRCLE}
      />
      <Tags.ProceedToDetails />
    </ListItemWrapper>
  ) : (<p />);
}

export default VehicleStatsItem;
